import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import api from '../api';
import { getSocket } from '../socket';
import LoadingSpinner from '../components/LoadingSpinner';
import BottomNav from '../components/BottomNav';
import Toast, { useToast } from '../components/Toast';
import StaffCallSheet from '../components/StaffCallSheet';

const STATUS_STEPS = [
  {
    key: 'pending',
    label: '주문접수',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/>
        <line x1="8" y1="18" x2="21" y2="18"/>
        <circle cx="3" cy="6" r="1" fill="currentColor"/>
        <circle cx="3" cy="12" r="1" fill="currentColor"/>
        <circle cx="3" cy="18" r="1" fill="currentColor"/>
      </svg>
    ),
  },
  {
    key: 'cooking',
    label: '조리중',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z"/>
        <path d="M12 6v6l4 2"/>
      </svg>
    ),
  },
  {
    key: 'served',
    label: '서빙완료',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <polyline points="20 6 9 17 4 12"/>
      </svg>
    ),
  },
];

const ORDER_STATUS_MAP = {
  pending: 0,
  accepted: 1,
  cooking: 1,
  ready: 2,
  served: 2,
  completed: 2,
};

function StatusBadge({ status }) {
  const config = {
    pending: { className: 'tag-order-pending', label: '접수 대기' },
    accepted: { className: 'tag-order-accepted', label: '주문 접수' },
    cooking: { className: 'tag-order-cooking', label: '조리 중' },
    ready: { className: 'tag-order-served', label: '준비 완료' },
    served: { className: 'tag-order-served', label: '서빙 완료' },
    completed: { className: 'tag-order-served', label: '완료' },
    cancelled: { className: 'tag-order-cancelled', label: '취소됨' },
  };
  const cfg = config[status] || config.pending;
  return <span className={`tag ${cfg.className} animate-scale-in`}>{cfg.label}</span>;
}

const toUtcDate = (str) => {
  if (!str) return null;
  if (!str.includes('Z') && !str.includes('+')) return new Date(str + 'Z');
  return new Date(str);
};

// Dutch pay modal
function DutchPayModal({ totalAmount, paidAmount, onClose, onConfirm }) {
  const [personCount, setPersonCount] = useState(2);
  const remaining = Math.max(0, totalAmount - paidAmount);
  const splitAmount = Math.ceil(totalAmount / personCount);
  const actualSplit = Math.min(splitAmount, remaining);

  return (
    <>
      <div className="overlay animate-fade-in" onClick={onClose} />
      <div className="menu-modal">
        <div className="menu-modal-inner" style={{ padding: 24 }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 24,
        }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>더치페이</h3>
          <button className="sheet-close-btn" style={{ position: 'static' }} onClick={onClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div style={{ fontSize: 15, color: 'var(--text-tertiary)', fontWeight: 500, marginBottom: 20, textAlign: 'center' }}>
          몇 명이서 나누시겠습니까?
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 28, marginBottom: 24 }}>
          <button
            className="qty-btn"
            onClick={() => setPersonCount(Math.max(2, personCount - 1))}
            style={{
              width: 44,
              height: 44,
              background: 'var(--border)',
              borderRadius: '50%',
              fontSize: 20,
            }}
          >
            -
          </button>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <span style={{ fontSize: 36, fontWeight: 800, color: 'var(--primary)' }}>
              {personCount}
            </span>
            <span style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>명</span>
          </div>
          <button
            className="qty-btn"
            onClick={() => setPersonCount(Math.min(20, personCount + 1))}
            style={{
              width: 44,
              height: 44,
              background: 'var(--border)',
              borderRadius: '50%',
              fontSize: 20,
            }}
          >
            +
          </button>
        </div>

        <div style={{
          background: 'var(--primary-light)',
          borderRadius: 'var(--radius-md)',
          padding: '14px 16px',
          marginBottom: 20,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 14, color: 'var(--text-tertiary)' }}>총 금액</span>
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{totalAmount.toLocaleString()}원</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 15, color: 'var(--primary)', fontWeight: 600 }}>1인당 금액</span>
            <span style={{ fontSize: 22, fontWeight: 800, color: 'var(--primary)' }}>
              {actualSplit.toLocaleString()}원
            </span>
          </div>
        </div>

        <button
          className="btn btn-primary btn-block btn-lg"
          onClick={() => onConfirm(actualSplit)}
        >
          {actualSplit.toLocaleString()}원 결제하기
        </button>
        </div>
      </div>
    </>
  );
}

export default function OrderStatusPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { storeId, tableId, addItem, paymentMode } = useCart();

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tableNumber, setTableNumber] = useState(null);

  const { toast, showToast } = useToast();

  const [staffCallOpen, setStaffCallOpen] = useState(false);
  const [staffCallCooldown, setStaffCallCooldown] = useState(false);
  const cooldownTimer = useRef(null);

  const [dutchPayOpen, setDutchPayOpen] = useState(false);

  const [cancellingOrderId, setCancellingOrderId] = useState(null);

  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  const fetchSession = useCallback(async () => {
    if (!sessionId || !uuidPattern.test(sessionId)) {
      setLoading(false);
      return;
    }
    try {
      const res = await api.get(`/sessions/${sessionId}/summary`);
      const result = res?.data || res;
      if (result?.session) {
        const s = result.session;
        setSession({
          tableNumber: s.table_number,
          tableId: s.table_id,
          storeId: s.store_id,
          totalAmount: s.total_amount || 0,
          paidAmount: s.paid_amount || 0,
          orders: (result.orders || []).map((o) => ({
            ...o,
            items: (o.items || []).map((item) => ({
              ...item,
              name: item.menu_name || item.name || '메뉴',
              price: item.unit_price || item.price || 0,
            })),
          })),
        });
        if (s.table_number != null) setTableNumber(s.table_number);
      }
    } catch {}
    finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  useEffect(() => {
    if (!storeId) return;
    let socket;
    try {
      socket = getSocket(storeId);
      socket.on('order_status_update', (data) => {
        if (data?.sessionId === sessionId) {
          fetchSession();
        }
      });
    } catch {}
    return () => {
      if (socket) socket.off('order_status_update');
    };
  }, [storeId, sessionId, fetchSession]);

  useEffect(() => {
    const interval = setInterval(fetchSession, 15000);
    return () => clearInterval(interval);
  }, [fetchSession]);

  useEffect(() => {
    return () => clearTimeout(cooldownTimer.current);
  }, []);

  const handleCancelOrder = async (orderId) => {
    setCancellingOrderId(orderId);
    try {
      await api.post(`/orders/${orderId}/cancel-customer`);
      showToast('주문이 취소되었습니다', 'success');
      fetchSession();
    } catch {
      showToast('주문 취소에 실패했습니다', 'error');
    } finally {
      setCancellingOrderId(null);
    }
  };

  const handleReorder = (order) => {
    const items = order.items || [];
    items.forEach((item) => {
      addItem({
        menuId: item.menu_id || item.id,
        name: item.name,
        price: item.price || item.unit_price || 0,
        quantity: item.quantity || 1,
        options: Array.isArray(item.options) ? item.options : [],
        imageUrl: item.imageUrl || item.image_url,
      });
    });
    showToast('장바구니에 담았습니다', 'success');
    if (storeId && tableId) {
      navigate(`/cart/${storeId}/${tableId}`);
    }
  };

  const handleStaffCall = async ({ type, message }) => {
    setStaffCallOpen(false);
    try {
      await api.post('/staff-calls', {
        store_id: session?.storeId || storeId,
        table_id: session?.tableId || tableId,
        table_number: tableNumber || session?.tableNumber,
        type,
        message,
      });
      showToast('호출 완료', 'success');
      setStaffCallCooldown(true);
      cooldownTimer.current = setTimeout(() => {
        setStaffCallCooldown(false);
      }, 30000);
    } catch {
      showToast('호출에 실패했습니다', 'error');
    }
  };

  const handleDutchPayConfirm = (splitAmount) => {
    setDutchPayOpen(false);
    navigate(`/payment/${sessionId}?split=${splitAmount}`);
  };

  if (loading) return <LoadingSpinner fullPage />;

  if (!session) {
    return (
      <div className="page">
        <header className="page-header">
          <div style={{ width: 36 }} />
          <span className="page-header-title">주문 현황</span>
          <div style={{ width: 36 }} />
        </header>
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/>
              <line x1="8" y1="18" x2="21" y2="18"/>
              <circle cx="3" cy="6" r="1" fill="currentColor"/>
              <circle cx="3" cy="12" r="1" fill="currentColor"/>
              <circle cx="3" cy="18" r="1" fill="currentColor"/>
            </svg>
          </div>
          <div className="empty-state-title">주문 내역이 없습니다</div>
        </div>
      </div>
    );
  }

  const orders = session.orders || [];
  const hasServed = orders.some((o) => o.status === 'served' || o.status === 'completed' || o.status === 'ready');

  const allStatuses = orders.map((o) => ORDER_STATUS_MAP[o.status] ?? 0);
  const maxStep = allStatuses.length > 0 ? Math.max(...allStatuses) : 0;

  return (
    <>
    <div className="page" style={{ paddingBottom: 'calc(60px + var(--safe-bottom))' }}>
      <header className="page-header">
        <div style={{ width: 36 }} />
        <span className="page-header-title">주문 현황</span>
        <button
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--text-tertiary)',
            padding: 4,
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={fetchSession}
          title="새로고침"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <polyline points="23 4 23 10 17 10" />
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
          </svg>
        </button>
      </header>

      {/* Order complete banner */}
      <div
        className="animate-fade-in-down"
        style={{
          background: 'var(--primary)',
          margin: '16px 20px',
          borderRadius: 'var(--radius-lg)',
          padding: '24px 20px',
          textAlign: 'center',
        }}
      >
        <div style={{
          width: 48, height: 48,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 12px',
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </div>
        <div style={{ fontSize: 17, fontWeight: 700, color: '#fff', marginBottom: 4 }}>
          주문이 완료되었습니다
        </div>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>
          테이블 {session.tableNumber || tableId}번
        </div>
      </div>

      {/* Orders list */}
      <div style={{ padding: '0 20px' }}>
        <div className="section-title">주문 내역</div>

        {orders.length === 0 ? (
          <div className="empty-state" style={{ padding: '40px 0' }}>
            <div className="empty-state-icon">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/>
                <line x1="8" y1="18" x2="21" y2="18"/>
                <circle cx="3" cy="6" r="1" fill="currentColor"/>
                <circle cx="3" cy="12" r="1" fill="currentColor"/>
                <circle cx="3" cy="18" r="1" fill="currentColor"/>
              </svg>
            </div>
            <div className="empty-state-title">주문 내역이 없습니다</div>
          </div>
        ) : (
          <div className="stagger-children" style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
            {orders.map((order, idx) => {
              const orderId = order._id || order.id;
              const isPending = order.status === 'pending';
              const isCompleted = order.status === 'served' || order.status === 'completed' || order.status === 'ready';
              const isCancelled = order.status === 'cancelled';

              return (
                <div
                  key={orderId || idx}
                  className="animate-fade-in-up"
                  style={{
                    background: 'var(--surface)',
                    borderRadius: 'var(--radius-lg)',
                    boxShadow: 'var(--shadow-sm)',
                    padding: 20,
                    animationDelay: `${idx * 60}ms`,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 12,
                    }}
                  >
                    <div style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>
                      주문 #{idx + 1} &middot;{' '}
                      {order.created_at || order.createdAt
                        ? toUtcDate(order.created_at || order.createdAt).toLocaleTimeString('ko-KR', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : ''}
                    </div>
                    <StatusBadge status={order.status} />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {(order.items || []).map((item, iIdx) => {
                      const opts = Array.isArray(item.options) ? item.options : [];
                      return (
                        <div key={iIdx} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 15 }}>
                            <span style={{ color: 'var(--text)' }}>
                              {item.name}{' '}
                              <span style={{ color: 'var(--text-tertiary)' }}>x{item.quantity}</span>
                            </span>
                            <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>
                              {(item.subtotal || (item.price || 0) * item.quantity).toLocaleString()}원
                            </span>
                          </div>
                          {opts.length > 0 && (
                            <div style={{ fontSize: 13, color: 'var(--text-tertiary)', paddingLeft: 4 }}>
                              {opts.map((o) => `+ ${o.name}${(o.price_delta || 0) > 0 ? ` (+${(o.price_delta).toLocaleString()}원)` : ''}`).join(' · ')}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="divider" style={{ margin: '12px 0 10px' }} />
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)' }}>소계</span>
                    <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>
                      {(order.total_amount || order.totalAmount || 0).toLocaleString()}원
                    </span>
                  </div>

                  {/* Action buttons */}
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    {isPending && !isCancelled && (
                      <button
                        className="btn btn-outline"
                        style={{ flex: 1, fontSize: 13, padding: '10px 12px', color: 'var(--danger)' }}
                        onClick={() => handleCancelOrder(orderId)}
                        disabled={cancellingOrderId === orderId}
                      >
                        {cancellingOrderId === orderId ? '취소 중...' : '주문 취소'}
                      </button>
                    )}

                    {isCompleted && !isCancelled && (
                      <button
                        className="btn btn-outline"
                        style={{ flex: 1, fontSize: 13, padding: '10px 12px' }}
                        onClick={() => handleReorder(order)}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ marginRight: 4 }}>
                          <polyline points="23 4 23 10 17 10" />
                          <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                        </svg>
                        다시 주문하기
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Total summary */}
        {orders.length > 0 && (
          <div
            style={{
              background: 'var(--bg-tertiary)',
              borderRadius: 'var(--radius-lg)',
              padding: '16px 20px',
              marginBottom: 16,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span style={{ fontWeight: 700, fontSize: 16, color: 'var(--text)' }}>총 주문 금액</span>
              <span className="price price-lg">
                {(session.totalAmount || 0).toLocaleString()}원
              </span>
            </div>
            {session.paidAmount > 0 && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: 8,
                }}
              >
                <span style={{ fontSize: 14, color: 'var(--success)', fontWeight: 600 }}>결제 완료</span>
                <span style={{ fontSize: 14, color: 'var(--success)', fontWeight: 700 }}>
                  -{(session.paidAmount || 0).toLocaleString()}원
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Payment buttons */}
      {paymentMode === 'post' && orders.length > 0 && (
        <div style={{ padding: '0 20px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button
            className="btn btn-primary btn-block btn-lg"
            onClick={() => navigate(`/payment/${sessionId}`)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ marginRight: 8 }}>
              <rect x="1" y="4" width="22" height="16" rx="2"/>
              <line x1="1" y1="10" x2="23" y2="10"/>
            </svg>
            셀프 계산하기
          </button>
          <button
            className="btn btn-secondary btn-block"
            onClick={() => setDutchPayOpen(true)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ marginRight: 6 }}>
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            더치페이
          </button>
        </div>
      )}

      </div>

      {/* Fixed elements outside .page */}
      <button
        className={staffCallCooldown ? '' : 'fab-btn'}
        onClick={() => !staffCallCooldown && setStaffCallOpen(true)}
        disabled={staffCallCooldown}
        style={{
          position: 'fixed',
          bottom: 'calc(60px + var(--safe-bottom) + 16px)',
          right: 'calc(50% - 240px + 16px)',
          width: 48,
          height: 48,
          borderRadius: '50%',
          background: staffCallCooldown ? 'var(--text-quaternary)' : 'var(--primary)',
          color: '#fff',
          border: 'none',
          cursor: staffCallCooldown ? 'not-allowed' : 'pointer',
          boxShadow: '0 4px 12px rgba(191,155,48,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 180,
          transition: 'opacity 0.15s ease',
          opacity: staffCallCooldown ? 0.5 : 1,
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
      </button>

      <StaffCallSheet
        open={staffCallOpen}
        onClose={() => setStaffCallOpen(false)}
        onSubmit={handleStaffCall}
      />

      {dutchPayOpen && (
        <DutchPayModal
          totalAmount={session.totalAmount || 0}
          paidAmount={session.paidAmount || 0}
          onClose={() => setDutchPayOpen(false)}
          onConfirm={handleDutchPayConfirm}
        />
      )}

      <BottomNav />
      <Toast {...toast} />
    </>
  );
}
